/** Persist-confirmation dialog for the batch grid — mirrors the per-tenant
 *  SaveConfigChangesDialog UX at cross-tenant scale: a per-tenant
 *  Added/Changed/Removed diff, a mandatory audit comment, and the
 *  all-or-nothing POST.
 *
 * On a 409 `batch_conflict` NOTHING was persisted: the dialog switches to a
 * conflict pane naming the drifted tenants; "Refresh & rebase" refetches the
 * bulk configs and REBASE_TENANTs every pending tenant — obsolete edits drop
 * silently (already applied server-side), surviving conflicts render inline
 * with per-entry "Keep mine" (KEEP_MINE) / "Drop" resolution, and Save stays
 * disabled while any stale entry remains.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import {
  MASKED_SECRET,
  isBatchConflict,
  persistBatch,
  type BatchConflictEntry,
} from '@/api/tourOperatorConfigs';
import { useSnackbar } from '@/components/Snackbar';

import { safePreview } from '../config/schema-grid/schemaOps';

import { useBatchData } from './BatchDataContext';
import {
  buildPersistRequest,
  changeSetReducer,
  hasStaleEdits,
  pendingTotals,
  UNSET,
  type ChangeSetAction,
  type PendingEdit,
} from './changeSet';
import { useChangeSet } from './ChangeSetContext';
import { canonicalize } from './pathOps';

interface TenantDiff {
  tenantId: number;
  name: string;
  added: Array<{ path: string; edit: PendingEdit }>;
  changed: Array<{ path: string; edit: PendingEdit }>;
  removed: Array<{ path: string; edit: PendingEdit }>;
}

function preview(value: unknown | typeof UNSET, secret: boolean): string {
  if (value === UNSET) return 'removed';
  if (secret && value !== undefined) return MASKED_SECRET;
  return safePreview(value, 60);
}

export function PersistDialog({
  open,
  onClose,
  secretKeys,
}: {
  open: boolean;
  onClose: () => void;
  /** Canonical keys whose values must render masked. */
  secretKeys: Set<string>;
}) {
  const { state, dispatch } = useChangeSet();
  const { tenantById, refetchConfigs } = useBatchData();
  const snackbar = useSnackbar();
  const queryClient = useQueryClient();

  const [comment, setComment] = useState('');
  const [touched, setTouched] = useState(false);
  const [conflicts, setConflicts] = useState<BatchConflictEntry[] | null>(null);
  const [rebasing, setRebasing] = useState(false);
  const [otherError, setOtherError] = useState<string | null>(null);

  const isSecret = (path: string): boolean => {
    try {
      return secretKeys.has(canonicalize(path));
    } catch {
      return false;
    }
  };

  const diffs = useMemo<TenantDiff[]>(() => {
    const out: TenantDiff[] = [];
    for (const [tidStr, tenantEdits] of Object.entries(state.edits)) {
      const tenantId = Number(tidStr);
      const diff: TenantDiff = {
        tenantId,
        name: tenantById.get(tenantId)?.name ?? `#${tenantId}`,
        added: [],
        changed: [],
        removed: [],
      };
      for (const path of Object.keys(tenantEdits).sort()) {
        const edit = tenantEdits[path];
        if (!edit) continue;
        if (edit.after === UNSET) diff.removed.push({ path, edit });
        else if (edit.before === undefined) diff.added.push({ path, edit });
        else diff.changed.push({ path, edit });
      }
      out.push(diff);
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.version, tenantById]);

  const totals = useMemo(
    () => pendingTotals(state),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.version],
  );
  const stale = useMemo(
    () => hasStaleEdits(state),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.version],
  );

  const trimmedComment = comment.trim();
  const isCommentValid = trimmedComment.length >= 3;

  const mutation = useMutation({ mutationFn: persistBatch });

  const canSubmit =
    !mutation.isPending && !rebasing && !stale && totals.edits > 0 && isCommentValid;

  function handleClose() {
    if (mutation.isPending || rebasing) return;
    setComment('');
    setTouched(false);
    setConflicts(null);
    setOtherError(null);
    mutation.reset();
    onClose();
  }

  function handleSave() {
    setTouched(true);
    if (!canSubmit) return;
    setOtherError(null);
    const applied = totals; // captured before RESET_ALL clears the store
    mutation.mutate(buildPersistRequest(state, trimmedComment), {
      onSuccess: () => {
        snackbar.show(
          `Applied ${applied.edits} change${applied.edits === 1 ? '' : 's'} across ${applied.tenants} tenant${applied.tenants === 1 ? '' : 's'}.`,
          'success',
        );
        dispatch({ type: 'RESET_ALL' });
        void queryClient.invalidateQueries({ queryKey: ['tourOperators', 'configsBulk'] });
        setComment('');
        setTouched(false);
        setConflicts(null);
        onClose();
      },
      onError: (err) => {
        if (isBatchConflict(err)) {
          setConflicts(err.body.errors);
        } else {
          setOtherError(err instanceof Error ? err.message : String(err));
        }
      },
    });
  }

  async function handleRebase() {
    setRebasing(true);
    setOtherError(null);
    try {
      const fresh = await refetchConfigs();
      if (!fresh) {
        setOtherError('Could not refresh tenant configs — please retry.');
        return;
      }
      const freshById = new Map(fresh.results.map((t) => [t.id, t]));
      // Rebase EVERY tenant with pending edits, not only the conflicted ones —
      // the fresh snapshot is the new baseline for the whole change set.
      const actions: ChangeSetAction[] = Object.keys(state.edits)
        .map(Number)
        .map((tenantId) => ({
          type: 'REBASE_TENANT' as const,
          tenantId,
          freshConfig: freshById.get(tenantId)?.config ?? null,
          freshTs: freshById.get(tenantId)?.config_last_modified_ts ?? null,
        }));
      // The reducer is pure — simulate to learn how many entries drop as
      // already-applied before dispatching the same actions for real.
      const simulated = actions.reduce(changeSetReducer, state);
      const dropped = pendingTotals(state).edits - pendingTotals(simulated).edits;
      for (const action of actions) dispatch(action);
      setConflicts(null);
      if (dropped > 0) {
        snackbar.show(
          `${dropped} pending edit${dropped === 1 ? '' : 's'} already applied on the server — dropped.`,
          'info',
        );
      }
    } finally {
      setRebasing(false);
    }
  }

  const conflictNames = (conflicts ?? []).map(
    (c) => tenantById.get(c.tour_operator)?.name ?? `#${c.tour_operator}`,
  );

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Persist batch configuration changes</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Applied atomically — if any tenant&apos;s config changed since you loaded it, nothing is
          saved.
        </Alert>

        {conflicts && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void handleRebase()}
                disabled={rebasing}
                startIcon={rebasing ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                Refresh &amp; rebase
              </Button>
            }
          >
            Nothing was saved — {conflictNames.length} tenant
            {conflictNames.length === 1 ? ' has' : 's have'} newer changes on the server:{' '}
            {conflictNames.join(', ')}. Refresh to rebase your edits onto the latest configs.
          </Alert>
        )}

        {stale && !conflicts && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Some edits conflict with newer server values — resolve each one below (“Keep mine” or
            “Drop”) before saving.
          </Alert>
        )}

        {totals.edits === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No pending changes — there&apos;s nothing to persist.
          </Alert>
        )}

        {diffs.map((diff) => (
          <Box key={diff.tenantId} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {diff.name}
            </Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
                bgcolor: 'background.default',
              }}
            >
              {(
                [
                  ['Added', diff.added, 'success.main'],
                  ['Changed', diff.changed, 'warning.main'],
                  ['Removed', diff.removed, 'error.main'],
                ] as const
              ).map(([label, entries, color]) =>
                entries.length === 0 ? null : (
                  <Box key={label} sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color, fontWeight: 600 }}>
                      {label} ({entries.length})
                    </Typography>
                    {entries.map(({ path, edit }) => {
                      const secret = isSecret(path);
                      return (
                        <Box
                          key={path}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontFamily: 'monospace',
                            fontSize: 12,
                            py: 0.25,
                          }}
                        >
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {path}
                          </Box>
                          <Box component="span" sx={{ color: 'text.secondary' }}>
                            {edit.stale ? (
                              <>
                                server now:{' '}
                                <Box component="span" sx={{ color: 'error.main' }}>
                                  {preview(edit.before, secret)}
                                </Box>
                                {' · mine: '}
                                <Box component="span" sx={{ color: 'success.main' }}>
                                  {preview(edit.after, secret)}
                                </Box>
                              </>
                            ) : (
                              <>
                                {preview(edit.before, secret)}
                                {' → '}
                                {preview(edit.after, secret)}
                              </>
                            )}
                          </Box>
                          {edit.stale && (
                            <>
                              <Chip label="conflict" size="small" color="warning" />
                              <Button
                                size="small"
                                onClick={() =>
                                  dispatch({ type: 'KEEP_MINE', tenantId: diff.tenantId, path })
                                }
                              >
                                Keep mine
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                onClick={() =>
                                  dispatch({ type: 'REMOVE_EDIT', tenantId: diff.tenantId, path })
                                }
                              >
                                Drop
                              </Button>
                            </>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ),
              )}
            </Box>
          </Box>
        ))}

        <Divider sx={{ my: 2 }} />

        <TextField
          label="Describe this change *"
          fullWidth
          multiline
          rows={2}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onBlur={() => setTouched(true)}
          helperText={
            touched && !isCommentValid
              ? 'Required — at least 3 characters.'
              : "Recorded on each tenant's config change history."
          }
          error={touched && !isCommentValid}
          inputProps={{ maxLength: 500 }}
          disabled={mutation.isPending || rebasing}
        />

        {otherError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {otherError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={mutation.isPending || rebasing}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSubmit}>
          {mutation.isPending
            ? 'Saving…'
            : totals.edits > 0
              ? `Save ${totals.edits} change${totals.edits === 1 ? '' : 's'}`
              : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
